import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import {
  Users,
  UserCircle,
  Search,
  ChevronRight,
  AlertCircle,
  UserPlus,
} from 'lucide-react';
import { Button } from '../../components/ui';
import { AddUserModal } from '../../components/users';

export function UsersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  const isSuperAdmin = user?.acl_care?.acl_care_superadmin === true;

  // Fetch users function (extracted for reuse)
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = isSuperAdmin
        ? await api.getAllACLUsers()
        : await api.getAllUsers(user.organization_id);

      const allUsers = result.users || result || [];
      // Filter out patients - only keep providers and staff
      const nonPatients = allUsers
        .filter(u => u.patient_id == null)
        .sort((a, b) => a.first_name.localeCompare(b.first_name));
      setUsers(nonPatients);
    } catch (err) {
      console.error('[UsersPage] Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, [isSuperAdmin]);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;

    const query = searchQuery.toLowerCase();
    return users.filter(u => {
      const name = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [users, searchQuery]);

  const handleUserClick = (clickedUser) => {
    navigate(`/users/${clickedUser.id}`);
  };

  const handleAddUser = () => {
    setShowAddModal(true);
  };

  const handleUserAdded = () => {
    // Refresh the list after adding a user
    fetchUsers();
  };


  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4 flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Users className="h-6 w-6 text-primary mr-2" />
          <h1 className="text-2xl font-bold text-gray-900">Admin</h1>
        </div>
        <Button onClick={handleAddUser}>
          <UserPlus className="h-4 w-4 mr-2" />
          Add Admin
        </Button>
      </div>

      {/* User List Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Search */}
        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search admins..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* User List */}
        <div className="divide-y divide-gray-100">
          {filteredUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <UserCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No admins found</p>
              {searchQuery && (
                <p className="text-sm mt-1">Try adjusting your search</p>
              )}
            </div>
          ) : (
            filteredUsers.map((u) => (
              <UserListItem
                key={u.id}
                user={u}
                onClick={() => handleUserClick(u)}
                isSuperAdmin={isSuperAdmin}
              />
            ))
          )}
        </div>
      </div>

      {/* Add User Modal */}
      <AddUserModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleUserAdded}
        organizationId={user?.organization_id}
      />
    </div>
  );
}

function UserListItem({ user, onClick, isSuperAdmin }) {
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown';
  const initials = `${(user.first_name || '?')[0]}${(user.last_name || '?')[0]}`.toUpperCase();
  const onboardingComplete = user.acl_care?.acl_onboarding_completed === true;

  return (
    <button
      onClick={onClick}
      className="w-full p-4 flex items-center hover:bg-gray-50 transition-colors text-left"
    >
      {/* Avatar */}
      {user.profile_photo ? (
        <img
          src={user.profile_photo}
          alt={fullName}
          className="h-12 w-12 rounded-full object-cover mr-4"
        />
      ) : (
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mr-4">
          <span className="text-primary font-semibold">{initials}</span>
        </div>
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 truncate">{fullName}</p>
        <p className="text-sm text-gray-500 truncate">{user.email}</p>

        {/* Status badges */}
        <div className="flex flex-wrap items-center gap-2 mt-1">
          {/* Onboarding status */}
          {onboardingComplete ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
              Onboarding Complete
            </span>
          ) : (
            <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
              Onboarding Incomplete
            </span>
          )}

          {/* Organization badge (super admin only) */}
          {isSuperAdmin && user.organization && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
              {user.organization.name}
            </span>
          )}
        </div>
      </div>

      <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
    </button>
  );
}